package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.layout.Box
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_copied
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poem_original
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.unit.LayoutDirection
import diana.soleil.design.iraniancanadienbazzar.core.model.Language
import kotlinx.coroutines.launch
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.common.ShareService
import diana.soleil.design.iraniancanadienbazzar.core.data.ShareConfig
import diana.soleil.design.iraniancanadienbazzar.core.model.Poem
import diana.soleil.design.iraniancanadienbazzar.core.model.Poet
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_error
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poems_empty_desc
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poems_empty_title
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poetry_empty_desc
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poetry_empty_title
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poetry_title
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poem_count
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poem_translation
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_share_message
import diana.soleil.design.iraniancanadienbazzar.core.model.formatCount
import androidx.compose.foundation.layout.size
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

/** Stateful entry for the poet shelf (`/gems/poetry`). */
@Composable
fun PoetryRoute(
    onBack: () -> Unit,
    onOpenPoet: (poetId: String) -> Unit,
    modifier: Modifier = Modifier,
    viewModel: PoetryViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    PoetryScreen(state = state, onBack = onBack, onOpenPoet = onOpenPoet, modifier = modifier)
}

/** The shelf: one card per poet — name, era, and the opening line of their biography. */
@Composable
fun PoetryScreen(
    state: PoetryUiState,
    onBack: () -> Unit,
    onOpenPoet: (poetId: String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val lang = AppTheme.language
    Column(
        modifier = modifier.fillMaxSize().background(AppTheme.colors.background)
            .statusBarsPadding().navigationBarsPadding(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        GemsSubTopBar(title = stringResource(Res.string.gems_poetry_title), onBack = onBack)
        when {
            state.loading -> LoadingState()
            state.error -> EmptyState(title = stringResource(Res.string.gems_error))
            state.poets.isEmpty() -> EmptyState(
                title = stringResource(Res.string.gems_poetry_empty_title),
                description = stringResource(Res.string.gems_poetry_empty_desc),
            )
            else -> LazyColumn(
                modifier = Modifier.widthIn(max = AppTheme.sizes.contentMaxWidthCompact).fillMaxWidth(),
                contentPadding = PaddingValues(AppTheme.spacing.lg),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
            ) {
                items(state.poets, key = { it.id }) { poet ->
                    val name = poet.name.resolve(lang)
                    val count = state.poemCounts[poet.id] ?: 0
                    AppCard(modifier = Modifier.fillMaxWidth(), onClick = { onOpenPoet(poet.id) }) {
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(AppTheme.spacing.md),
                            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
                            verticalAlignment = Alignment.Top,
                        ) {
                            // A gold seal with the poet's initial: the shelf has no portraits (no
                            // rights-clear art for most of these poets), and a row of identical
                            // text cards read as a settings list, not a bookshelf.
                            Box(
                                modifier = Modifier
                                    .size(AppTheme.sizes.avatarMd)
                                    .background(AppTheme.colors.secondaryContainer, AppTheme.shapes.pill),
                                contentAlignment = Alignment.Center,
                            ) {
                                Text(
                                    text = name.take(1),
                                    style = AppTheme.typography.headlineMd,
                                    color = AppTheme.colors.onSecondaryContainer,
                                )
                            }
                            Column(
                                modifier = Modifier.weight(1f),
                                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                            ) {
                                Row(
                                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                                    verticalAlignment = Alignment.CenterVertically,
                                ) {
                                    Text(
                                        text = name,
                                        style = AppTheme.typography.headlineMd,
                                        color = AppTheme.colors.onSurface,
                                        modifier = Modifier.weight(1f),
                                    )
                                    if (count > 0) {
                                        Text(
                                            text = pluralStringResource(Res.plurals.gems_poem_count, count, lang.formatCount(count)),
                                            style = AppTheme.typography.labelSm,
                                            color = AppTheme.colors.onSurfaceVariant,
                                        )
                                    }
                                }
                                poet.era.resolve(lang).takeIf { it.isNotBlank() }?.let {
                                    Text(
                                        text = it,
                                        style = AppTheme.typography.labelMd,
                                        color = AppTheme.colors.primary,
                                    )
                                }
                                poet.bio.resolve(lang).takeIf { it.isNotBlank() }?.let {
                                    Text(
                                        text = it,
                                        style = AppTheme.typography.bodySm,
                                        color = AppTheme.colors.onSurfaceVariant,
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

/** Stateful entry for one poet's poems (`/gems/poetry/{poetId}`). */
@Composable
fun PoetPoemsRoute(
    poetId: String,
    onBack: () -> Unit,
    onOpenPoem: (poemId: String) -> Unit,
    modifier: Modifier = Modifier,
    viewModel: PoetPoemsViewModel = koinViewModel { parametersOf(poetId) },
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    PoetPoemsScreen(state = state, onBack = onBack, onOpenPoem = onOpenPoem, modifier = modifier)
}

/** One poet: their name as the title, then a card per poem showing its first lines. */
@Composable
fun PoetPoemsScreen(
    state: PoetPoemsUiState,
    onBack: () -> Unit,
    onOpenPoem: (poemId: String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val lang = AppTheme.language
    Column(
        modifier = modifier.fillMaxSize().background(AppTheme.colors.background)
            .statusBarsPadding().navigationBarsPadding(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        GemsSubTopBar(
            title = state.poet?.name?.resolve(lang) ?: stringResource(Res.string.gems_poetry_title),
            onBack = onBack,
        )
        when {
            state.loading -> LoadingState()
            state.error -> EmptyState(title = stringResource(Res.string.gems_error))
            state.poems.isEmpty() -> EmptyState(
                title = stringResource(Res.string.gems_poems_empty_title),
                description = stringResource(Res.string.gems_poems_empty_desc),
            )
            else -> LazyColumn(
                modifier = Modifier.widthIn(max = AppTheme.sizes.contentMaxWidthCompact).fillMaxWidth(),
                contentPadding = PaddingValues(AppTheme.spacing.lg),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
            ) {
                // The poet's own line first: the shelf card had the bio, and losing it the moment
                // you tap in made every poet feel interchangeable.
                state.poet?.let { poet ->
                    val era = poet.era.resolve(lang)
                    val bio = poet.bio.resolve(lang)
                    if (era.isNotBlank() || bio.isNotBlank()) {
                        item("poet-header") {
                            Column(
                                modifier = Modifier.fillMaxWidth().padding(bottom = AppTheme.spacing.sm),
                                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                            ) {
                                if (era.isNotBlank()) {
                                    Text(text = era, style = AppTheme.typography.labelMd, color = AppTheme.colors.primary)
                                }
                                if (bio.isNotBlank()) {
                                    Text(text = bio, style = AppTheme.typography.bodyMd, color = AppTheme.colors.onSurfaceVariant)
                                }
                            }
                        }
                    }
                }
                items(state.poems, key = { it.id }) { poem ->
                    AppCard(modifier = Modifier.fillMaxWidth(), onClick = { onOpenPoem(poem.id) }) {
                        Column(
                            modifier = Modifier.fillMaxWidth().padding(AppTheme.spacing.md),
                            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                        ) {
                            Row(
                                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                Text(
                                    text = poem.title.resolve(lang),
                                    style = AppTheme.typography.headlineMd,
                                    color = AppTheme.colors.onSurface,
                                    modifier = Modifier.weight(1f),
                                )
                                Text(
                                    text = poem.form.label(),
                                    style = AppTheme.typography.labelSm,
                                    color = AppTheme.colors.onSurfaceVariant,
                                )
                            }
                            // The opening beyt in Persian is how a reader recognises the poem; in a
                            // Farsi session the body already is Persian, so show it in the body scale.
                            val opening = poem.body.fa?.lines()?.filter { it.isNotBlank() }?.take(2).orEmpty()
                            if (lang != Language.FA && opening.isNotEmpty()) {
                                PersianOpeningLines(opening)
                            } else {
                                Text(
                                    text = poem.body.resolve(lang).lines().filter { it.isNotBlank() }.take(2).joinToString("\n"),
                                    style = AppTheme.typography.bodySm,
                                    color = AppTheme.colors.onSurfaceVariant,
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

/** Stateful entry for one poem (`/gems/poem/{poemId}`). */
@Composable
fun PoemDetailRoute(
    poemId: String,
    onBack: () -> Unit,
    onRequireSignIn: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: PoemDetailViewModel = koinViewModel { parametersOf(poemId) },
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val shareService = koinInject<ShareService>()
    val shareConfig = koinInject<ShareConfig>()
    val lang = AppTheme.language
    LaunchedEffect(state.signInRequired) {
        if (state.signInRequired) { viewModel.consumeSignInGate(); onRequireSignIn() }
    }
    LaunchedEffect(state.reportStatus) {
        if (state.reportStatus == GemReportStatus.SignInRequired) { viewModel.resetReport(); onRequireSignIn() }
    }
    val shareTemplate = stringResource(Res.string.gems_share_message)
    PoemDetailScreen(
        state = state,
        onBack = onBack,
        onToggleLike = viewModel::toggleLike,
        onToggleSave = viewModel::toggleSave,
        onShare = { poem ->
            shareService.share(
                shareTemplate
                    .replace("%1\$s", poem.title.resolve(lang))
                    .replace("%2\$s", shareConfig.poemLink(poem.id)),
            )
        },
        onReport = viewModel::report,
        onResetReport = viewModel::resetReport,
        modifier = modifier,
    )
}

/** The reading view: title, poet, the verse as typed (line breaks preserved), the note, actions. */
@Composable
fun PoemDetailScreen(
    state: PoemDetailUiState,
    onBack: () -> Unit,
    onToggleLike: () -> Unit,
    onToggleSave: () -> Unit,
    onShare: (Poem) -> Unit,
    onReport: (reason: String) -> Unit,
    onResetReport: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val lang = AppTheme.language
    val clipboard = LocalClipboardManager.current
    val snackbarHostState = remember { SnackbarHostState() }
    val scope = rememberCoroutineScope()
    val copiedMessage = stringResource(Res.string.gems_copied)
    var showReport by remember { mutableStateOf(false) }

    Box(modifier = modifier.fillMaxSize().background(AppTheme.colors.background)) {
        Column(
            modifier = Modifier.fillMaxSize().statusBarsPadding().navigationBarsPadding(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            GemsSubTopBar(
                title = state.poem?.title?.resolve(lang) ?: stringResource(Res.string.gems_poetry_title),
                onBack = onBack,
            )
            when {
                state.loading -> LoadingState()
                state.error || state.poem == null -> EmptyState(title = stringResource(Res.string.gems_error))
                else -> {
                    val poem = state.poem
                    val persian = poem.body.fa?.lines()?.filter { it.isNotBlank() }.orEmpty()
                    // The poem is the Persian text. In a Farsi session that IS the body; in an English
                    // or French session it comes first and the translation follows as the way in.
                    val translation = poem.body.resolve(lang).takeIf { lang != Language.FA && persian.isNotEmpty() }
                    Column(
                        modifier = Modifier
                            .widthIn(max = AppTheme.sizes.contentMaxWidthCompact)
                            .fillMaxWidth()
                            .verticalScroll(rememberScrollState())
                            .padding(AppTheme.spacing.lg),
                        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg),
                    ) {
                        GemByline(
                            parts = listOf(
                                state.poet?.name?.resolve(lang).orEmpty(),
                                state.poet?.era?.resolve(lang).orEmpty(),
                                poem.form.label(),
                            ),
                        )
                        if (persian.isNotEmpty()) {
                            PersianVersePanel(lines = persian)
                        } else {
                            // No Persian text on this document — show what we have, as authored.
                            Text(
                                text = poem.body.resolve(lang),
                                style = AppTheme.typography.bodyLg,
                                color = AppTheme.colors.onSurface,
                            )
                        }
                        translation?.let {
                            Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs)) {
                                Text(
                                    text = stringResource(Res.string.gems_poem_translation),
                                    style = AppTheme.typography.labelSm,
                                    color = AppTheme.colors.onSurfaceVariant,
                                )
                                // Rendered exactly as authored so line breaks stay where the
                                // translator put them.
                                Text(
                                    text = it,
                                    style = AppTheme.typography.bodyLg,
                                    color = AppTheme.colors.onSurface,
                                )
                            }
                        }
                        // Actions sit under the verse: read first, then like, save or share it.
                        GemDetailRail(
                            likeCount = poem.likeCount,
                            liked = state.liked,
                            saved = state.saved,
                            onToggleLike = onToggleLike,
                            onToggleSave = onToggleSave,
                            onCopy = {
                                clipboard.setText(AnnotatedString(poem.body.resolve(lang)))
                                scope.launch { snackbarHostState.showSnackbar(copiedMessage) }
                            },
                            onShare = { onShare(poem) },
                            onReport = { showReport = true },
                        )
                        poem.note?.resolve(lang)?.takeIf { it.isNotBlank() }?.let {
                            Text(
                                text = it,
                                style = AppTheme.typography.bodySm,
                                color = AppTheme.colors.onSurfaceVariant,
                            )
                        }
                    }
                }
            }
        }
        SnackbarHost(hostState = snackbarHostState, modifier = Modifier.align(Alignment.BottomCenter))
    }

    if (showReport) {
        GemReportSheet(
            status = state.reportStatus,
            onSubmit = onReport,
            onDismiss = { showReport = false; onResetReport() },
        )
    }
}

/** Shared back-bar for the gems sub-screens (same shape as the Shahnameh browser's header). */
@Composable
internal fun GemsSubTopBar(title: String, onBack: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = AppTheme.spacing.md, vertical = AppTheme.spacing.sm),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
    ) {
        AppIconButton(
            iconName = "arrow_back",
            contentDescription = stringResource(Res.string.gems_back_a11y),
            onClick = onBack,
            tint = AppTheme.colors.onSurface,
        )
        Text(
            text = title,
            style = AppTheme.typography.headlineLg,
            color = AppTheme.colors.onSurface,
            modifier = Modifier.weight(1f),
        )
    }
}
